import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { MotorLogCard } from '../components/waya/MotorLogCard';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Bot, ShieldAlert, Lock, Plus, Trash2, Play, AlertTriangle, Loader2, X, Ban, Pencil,
  ShieldCheck, Zap, CheckCircle2, Search,
} from 'lucide-react';

const ALERTAS = [
  { key: 'MONTO_MUY_PEQUEÑO', label: 'Monto muy pequeño' },
  { key: 'MONTO_PEQUEÑO', label: 'Monto pequeño' },
  { key: 'NOCTURNA', label: 'Nocturna' },
  { key: 'CUENTA_NUEVA', label: 'Cuenta nueva' },
  { key: 'CUIT_VIGILADO', label: 'CUIT vigilado' },
];
const ALERTA_LABEL = Object.fromEntries(ALERTAS.map(a => [a.key, a.label]));
const emptyRegla = () => ({
  nombre: '', activa: true, accion: 'BLOQUEO', modo: 'PROPONER',
  tipo_persona: 'TODAS',
  ventanaTipo: 'TODO', ventanaHoras: 24,
  condiciones: [{ tipo: 'ALERTA', alerta: 'MONTO_PEQUEÑO', valor: 10 }],
});

const TIPO_PERSONA_LABEL = { JURIDICA: 'Persona jurídica', FISICA: 'Persona física' };
const pad2 = (n) => String(n).padStart(2, '0');

const condLabel = (c) => {
  if (c.tipo === 'ALERTA') return `${ALERTA_LABEL[c.alerta] || c.alerta} ≥ ${c.valor}`;
  if (c.tipo === 'MONTO_ACUM') return `Monto acumulado ≥ $${Number(c.valor).toLocaleString('es-AR')}`;
  const extras = [];
  if (c.hora_desde != null && c.hora_hasta != null) extras.push(`${pad2(c.hora_desde)}–${pad2(c.hora_hasta)}hs`);
  if (c.monto_max != null) extras.push(`< $${Number(c.monto_max).toLocaleString('es-AR')}`);
  return `≥ ${c.valor} mov.${extras.length ? ' ' + extras.join(' ') : ''}`;
};

const WayaReglasAuto = () => {
  const [config, setConfig] = useState(null);
  const [reglas, setReglas] = useState([]);
  const [exclusiones, setExclusiones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rule builder dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyRegla());
  const [saving, setSaving] = useState(false);

  // Exclusion form
  const [excTipo, setExcTipo] = useState('CVU');
  const [excValor, setExcValor] = useState('');
  const [excNota, setExcNota] = useState('');
  const [excFiltro, setExcFiltro] = useState('');

  // Simulation
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Ejecución real (aprobación manual)
  const [ejecutando, setEjecutando] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // null = todas; [cvu] = una

  const esAdmin = !!config?.es_admin;

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, e] = await Promise.all([
        api.get('/waya-auto/config'),
        api.get('/waya-auto/reglas'),
        api.get('/waya-auto/exclusiones'),
      ]);
      setConfig(c.data);
      setReglas(r.data?.reglas || []);
      setExclusiones(e.data?.exclusiones || []);
    } catch { toast.error('No se pudo cargar la configuración'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleModoGlobal = async (activo) => {
    const nuevo = activo ? 'ACTIVO' : 'DRY_RUN';
    try {
      await api.put('/waya-auto/config', {
        modo_global: nuevo,
        max_por_corrida: config.max_por_corrida,
      });
      setConfig(c => ({ ...c, modo_global: nuevo }));
      toast.success(nuevo === 'ACTIVO' ? 'Bloqueos automáticos ACTIVADOS' : 'Modo simulación (dry-run)');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cambiar el modo'); }
  };

  const guardarMaxPorCorrida = async (val) => {
    const n = Math.max(1, parseInt(val, 10) || 1);
    try {
      await api.put('/waya-auto/config', {
        modo_global: config.modo_global, max_por_corrida: n,
      });
      setConfig(c => ({ ...c, max_por_corrida: n }));
      toast.success(`Tope automático: ${n} por corrida`);
    } catch { toast.error('No se pudo guardar el tope'); }
  };

  // ---- Rule builder ----
  const abrirNueva = () => { setEditId(null); setForm(emptyRegla()); setDlgOpen(true); };
  const abrirEditar = (r) => {
    setEditId(r.id);
    setForm({
      nombre: r.nombre, activa: r.activa, accion: r.accion, modo: r.modo,
      tipo_persona: r.tipo_persona || 'TODAS',
      ventanaTipo: r.ventana === 'TODO' ? 'TODO' : 'HORAS',
      ventanaHoras: r.ventana === 'TODO' ? 24 : Number(r.ventana),
      condiciones: (r.condiciones || []).map(c => ({ ...c })),
    });
    setDlgOpen(true);
  };
  const setCond = (i, patch) => setForm(f => ({ ...f, condiciones: f.condiciones.map((c, j) => j === i ? { ...c, ...patch } : c) }));
  const addCond = () => setForm(f => ({ ...f, condiciones: [...f.condiciones, { tipo: 'ALERTA', alerta: 'NOCTURNA', valor: 1 }] }));
  const removeCond = (i) => setForm(f => ({ ...f, condiciones: f.condiciones.filter((_, j) => j !== i) }));

  const guardarRegla = async () => {
    if (!form.nombre.trim()) { toast.error('Poné un nombre a la regla'); return; }
    if (!form.condiciones.length) { toast.error('Agregá al menos una condición'); return; }
    setSaving(true);
    const payload = {
      nombre: form.nombre.trim(), activa: form.activa, accion: form.accion, modo: form.modo,
      tipo_persona: form.tipo_persona || 'TODAS',
      ventana: form.ventanaTipo === 'TODO' ? 'TODO' : String(Number(form.ventanaHoras) || 24),
      condiciones: form.condiciones.map(c => {
        const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
        const esCant = c.tipo === 'CANT_MOV';
        return {
          tipo: c.tipo, alerta: c.tipo === 'ALERTA' ? c.alerta : null, valor: Number(c.valor) || 0,
          hora_desde: esCant ? num(c.hora_desde) : null,
          hora_hasta: esCant ? num(c.hora_hasta) : null,
          monto_max: esCant ? num(c.monto_max) : null,
        };
      }),
    };
    try {
      if (editId) await api.put(`/waya-auto/reglas/${editId}`, payload);
      else await api.post('/waya-auto/reglas', payload);
      toast.success(editId ? 'Regla actualizada' : 'Regla creada');
      setDlgOpen(false); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar la regla'); }
    finally { setSaving(false); }
  };

  const toggleActiva = async (r) => {
    try {
      await api.put(`/waya-auto/reglas/${r.id}`, { ...r, activa: !r.activa });
      setReglas(prev => prev.map(x => x.id === r.id ? { ...x, activa: !x.activa } : x));
    } catch { toast.error('No se pudo cambiar'); }
  };
  const eliminarRegla = async (r) => {
    if (!window.confirm(`¿Eliminar la regla "${r.nombre}"?`)) return;
    try { await api.delete(`/waya-auto/reglas/${r.id}`); setReglas(prev => prev.filter(x => x.id !== r.id)); toast.success('Regla eliminada'); }
    catch { toast.error('No se pudo eliminar'); }
  };

  // ---- Exclusiones ----
  const addExclusion = async () => {
    if (!excValor.trim()) { toast.error('Ingresá un valor'); return; }
    try {
      const r = await api.post('/waya-auto/exclusiones', { tipo: excTipo, valor: excValor.trim(), nota: excNota.trim() || null });
      setExclusiones(prev => [r.data, ...prev]); setExcValor(''); setExcNota('');
      toast.success('Exclusión agregada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar'); }
  };
  const removeExclusion = async (id) => {
    try { await api.delete(`/waya-auto/exclusiones/${id}`); setExclusiones(prev => prev.filter(x => x.id !== id)); }
    catch { toast.error('No se pudo eliminar'); }
  };

  // ---- Simulación ----
  const evaluar = async () => {
    setSimLoading(true);
    try { const r = await api.post('/waya-auto/evaluar'); setSim(r.data); }
    catch { toast.error('No se pudo evaluar'); }
    finally { setSimLoading(false); }
  };

  // ---- Ejecución real (aprobación manual) ----
  const aplicarianDecisiones = (sim?.decisiones || []).filter(d => d.aplicaria);
  const objetivos = confirmTarget
    ? aplicarianDecisiones.filter(d => confirmTarget.includes(d.cvu))
    : aplicarianDecisiones;
  const nCierre = objetivos.filter(d => d.accion === 'CIERRE').length;
  const nBloqueo = objetivos.length - nCierre;

  const pedirConfirmacion = (cvus) => { setConfirmTarget(cvus); setConfirmOpen(true); };
  const confirmarEjecucion = async () => {
    setEjecutando(true);
    try {
      const payload = confirmTarget ? { cvus: confirmTarget } : {};
      const r = await api.post('/waya-auto/ejecutar', payload);
      toast.success(`Ejecutado: ${r.data.bloqueos || 0} bloqueo(s), ${r.data.cierres || 0} cierre(s)`);
      setConfirmOpen(false); setConfirmTarget(null);
      await evaluar();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo ejecutar'); }
    finally { setEjecutando(false); }
  };

  if (loading) return <div className="p-10 text-center text-slate-400" data-testid="waya-auto-loading"><Loader2 size={26} className="mx-auto animate-spin" /></div>;

  const activo = config?.modo_global === 'ACTIVO';

  return (
    <div className="space-y-4" data-testid="waya-auto-panel">
      {/* Estado del motor */}
      <Card className={`p-4 border ${activo ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid="waya-auto-estado">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${activo ? 'bg-red-600' : 'bg-slate-400'} text-white`}><Bot size={20} /></div>
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                Bloqueo automático
                <Badge className={activo ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}>
                  {activo ? 'ACTIVO' : 'Simulación (dry-run)'}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {activo
                  ? 'El motor ejecuta acciones reales según las reglas.'
                  : 'El motor sólo simula: no bloquea ni cierra cuentas.'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {esAdmin ? (
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <span className={activo ? 'text-red-700' : 'text-slate-500'}>Ejecutar bloqueos</span>
                <Switch checked={activo} onCheckedChange={toggleModoGlobal} data-testid="waya-auto-master-switch" />
              </label>
            ) : (
              <span className="text-xs text-slate-400 flex items-center gap-1"><Lock size={13} /> Solo un administrador puede activarlo</span>
            )}
          </div>
        </div>
        {activo && (
          <div className="mt-3 text-xs text-red-800 bg-red-100 border border-red-200 rounded-md p-2 flex items-start gap-2" data-testid="waya-auto-warning">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" /> Atención: con el motor ACTIVO se crean casos y se ejecutan bloqueos/cierres en Waya automáticamente.
          </div>
        )}
        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid="waya-auto-nota-exclusiones">
          <ShieldCheck size={13} /> Solo se salvan del bloqueo automático las cuentas de la lista de exclusiones (más abajo).
        </p>
        <p className="mt-1 text-xs text-slate-500 flex items-center gap-1.5" data-testid="waya-auto-nota-ganadora">
          <ShieldCheck size={13} /> Si una cuenta cumple varias reglas, alcanza con que una esté en "Ejecuta" para que se bloquee sola.
        </p>
        {esAdmin && (
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 flex-wrap">
            <span>Tope de seguridad (máx. acciones automáticas por corrida):</span>
            <Input
              type="number" min={1}
              defaultValue={config?.max_por_corrida ?? 20}
              onBlur={e => guardarMaxPorCorrida(e.target.value)}
              className="w-20 h-8"
              data-testid="waya-auto-max-corrida"
            />
            <span className="text-slate-400">Evita que una regla mal configurada bloquee muchas cuentas de golpe (solo aplica a la ejecución automática).</span>
          </div>
        )}
      </Card>

      {/* Reglas */}
      <Card className="p-4 bg-white" data-testid="waya-auto-reglas">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 text-sm">Reglas ({reglas.length})</h3>
          {esAdmin && <Button size="sm" onClick={abrirNueva} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="waya-auto-nueva-regla"><Plus size={14} className="mr-1.5" /> Nueva regla</Button>}
        </div>
        {reglas.length === 0 ? (
          <div className="text-center text-slate-400 py-8 text-sm">Todavía no hay reglas. Creá la primera.</div>
        ) : (
          <div className="space-y-2">
            {reglas.map(r => (
              <div key={r.id} className="flex items-start justify-between gap-3 border rounded-md p-3 hover:bg-slate-50" data-testid={`waya-auto-regla-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800 text-sm">{r.nombre}</span>
                    <Badge className={r.accion === 'CIERRE' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}>
                      {r.accion === 'CIERRE' ? 'Cierre definitivo' : 'Bloqueo preventivo'}
                    </Badge>
                    {r.tipo_persona && r.tipo_persona !== 'TODAS' && (
                      <Badge className="bg-purple-100 text-purple-700">{TIPO_PERSONA_LABEL[r.tipo_persona] || r.tipo_persona}</Badge>
                    )}
                    <Badge className="bg-slate-100 text-slate-600">{r.modo === 'EJECUTAR' ? 'Ejecuta' : 'Propone'}</Badge>
                    <Badge className="bg-slate-100 text-slate-500">{r.ventana === 'TODO' ? 'Todo pendiente' : `Últimas ${r.ventana}h`}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(r.condiciones || []).map((c, i) => (
                      <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{condLabel(c)}</span>
                    ))}
                  </div>
                </div>
                {esAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={r.activa} onCheckedChange={() => toggleActiva(r)} data-testid={`waya-auto-regla-activa-${r.id}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => abrirEditar(r)} data-testid={`waya-auto-regla-editar-${r.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => eliminarRegla(r)} data-testid={`waya-auto-regla-eliminar-${r.id}`}><Trash2 size={14} /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Exclusiones */}
      <Card className="p-4 bg-white" data-testid="waya-auto-exclusiones">
        <h3 className="font-semibold text-slate-800 text-sm mb-3">Exclusiones ({exclusiones.length})</h3>
        {esAdmin && (
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={excTipo} onValueChange={setExcTipo}>
                <SelectTrigger className="w-40 h-9 mt-1" data-testid="waya-auto-exc-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CVU">CVU</SelectItem>
                  <SelectItem value="CUIT">CUIT</SelectItem>
                  <SelectItem value="RAZON_SOCIAL">Razón social / Nombre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Valor</Label>
              <Input value={excValor} onChange={e => setExcValor(e.target.value)} placeholder={excTipo === 'RAZON_SOCIAL' ? 'Nombre o parte del nombre' : 'Número'} className="mt-1 h-9" data-testid="waya-auto-exc-valor" />
            </div>
            <Button size="sm" onClick={addExclusion} className="bg-sky-600 hover:bg-sky-700 text-white h-9" data-testid="waya-auto-exc-add"><Plus size={14} className="mr-1.5" /> Agregar</Button>
          </div>
        )}
        {exclusiones.length === 0 ? (
          <div className="text-center text-slate-400 py-4 text-sm">Sin exclusiones.</div>
        ) : (
          <>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={excFiltro} onChange={e => setExcFiltro(e.target.value)} placeholder="Buscar por valor o nota…" className="pl-8 h-8 text-xs" data-testid="waya-auto-exc-filtro" />
            </div>
            {(() => {
              const f = excFiltro.trim().toLowerCase();
              const filtradas = exclusiones.filter(e => !f || (`${e.valor} ${e.nota || ''}`.toLowerCase().includes(f)));
              const labels = { CVU: 'CVU', CUIT: 'CUIT', RAZON_SOCIAL: 'Nombre / Razón social' };
              const grupos = { CVU: [], CUIT: [], RAZON_SOCIAL: [] };
              filtradas.forEach(e => { (grupos[e.tipo] = grupos[e.tipo] || []).push(e); });
              if (!filtradas.length) return <div className="text-center text-slate-400 py-3 text-xs">Sin coincidencias.</div>;
              return (
                <div className="space-y-3 max-h-[340px] overflow-auto pr-1">
                  {Object.keys(labels).filter(t => (grupos[t] || []).length).map(t => (
                    <div key={t}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Badge className="bg-slate-700 text-white text-[9px]">{labels[t]}</Badge>
                        <span className="text-[11px] text-slate-400">{grupos[t].length}</span>
                      </div>
                      <div className="rounded-md border divide-y divide-slate-100">
                        {grupos[t].map(e => (
                          <div key={e.id} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50" data-testid={`waya-auto-exc-${e.id}`}>
                            <span className={`text-xs text-slate-700 truncate flex-1 ${t === 'RAZON_SOCIAL' ? '' : 'font-mono'}`}>{e.valor}</span>
                            {e.nota && <span className="text-[10px] text-slate-400 truncate max-w-[40%]" title={e.nota}>{e.nota}</span>}
                            {esAdmin && <button onClick={() => removeExclusion(e.id)} className="text-slate-300 hover:text-red-600 shrink-0" data-testid={`waya-auto-exc-del-${e.id}`}><X size={14} /></button>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </Card>

      {/* Simulación / Ejecución */}
      <Card className="p-4 bg-white" data-testid="waya-auto-simulacion">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">Simulación y ejecución</h3>
            <p className="text-xs text-slate-500">Evaluá qué haría el motor con las alertas pendientes y aplicá las propuestas (aprobación manual).</p>
          </div>
          <div className="flex items-center gap-2">
            {esAdmin && aplicarianDecisiones.length > 0 && (
              <Button size="sm" onClick={() => pedirConfirmacion(null)} disabled={ejecutando} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-auto-aplicar-todas-btn">
                {ejecutando ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />} Aplicar todas ({aplicarianDecisiones.length})
              </Button>
            )}
            <Button size="sm" onClick={evaluar} disabled={simLoading} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="waya-auto-evaluar-btn">
              {simLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Play size={14} className="mr-1.5" />} Evaluar ahora
            </Button>
          </div>
        </div>
        {sim && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <div className="rounded-md border p-2 text-center"><div className="text-lg font-bold text-slate-800">{sim.total_cuentas}</div><div className="text-[10px] uppercase text-slate-400">Cuentas</div></div>
              <div className="rounded-md border p-2 text-center"><div className="text-lg font-bold text-blue-600">{sim.resumen?.BLOQUEO || 0}</div><div className="text-[10px] uppercase text-slate-400">Bloquearía</div></div>
              <div className="rounded-md border p-2 text-center"><div className="text-lg font-bold text-red-600">{sim.resumen?.CIERRE || 0}</div><div className="text-[10px] uppercase text-slate-400">Cerraría</div></div>
              <div className="rounded-md border p-2 text-center"><div className="text-lg font-bold text-slate-500">{(sim.resumen?.excluidas || 0) + (sim.resumen?.con_caso || 0)}</div><div className="text-[10px] uppercase text-slate-400">Omitidas</div></div>
            </div>
            {sim.coincidencias === 0 ? (
              <div className="text-center text-slate-400 py-6 text-sm">Ninguna cuenta coincide con las reglas activas.</div>
            ) : (
              <div className="max-h-[45vh] overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Cuenta</th>
                      <th className="text-left px-3 py-2 font-semibold">Alertas</th>
                      <th className="text-left px-3 py-2 font-semibold">Regla(s)</th>
                      <th className="text-left px-3 py-2 font-semibold">Acción</th>
                      {esAdmin && <th className="text-right px-3 py-2 font-semibold">Aplicar</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sim.decisiones.map((d, i) => (
                      <tr key={`${d.cvu}-${i}`} className={d.aplicaria ? '' : 'opacity-50'} data-testid={`waya-auto-sim-row-${i}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{d.nombre || '(sin nombre)'}</div>
                          <div className="text-[10px] text-slate-400">{d.cuit} · …{String(d.cvu).slice(-6)}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(d.conteos || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                              <span key={k} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1">{ALERTA_LABEL[k] || k}: {v}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{(d.reglas || []).join(', ')}</td>
                        <td className="px-3 py-2">
                          {d.aplicaria ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge className={d.accion === 'CIERRE' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}>{d.accion === 'CIERRE' ? 'Cerraría' : 'Bloquearía'}</Badge>
                              <Badge className={d.modo === 'EJECUTAR' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'} title={d.modo === 'EJECUTAR' ? 'Se ejecuta sola cuando el motor está ACTIVO' : 'Requiere aprobación manual'}>
                                {d.modo === 'EJECUTAR' ? <><Zap size={10} className="mr-0.5" /> Auto</> : 'Propone'}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-[11px] text-slate-400 flex items-center gap-1"><Ban size={12} /> {d.motivo_omision}</span>
                          )}
                        </td>
                        {esAdmin && (
                          <td className="px-3 py-2 text-right">
                            {d.aplicaria && (
                              <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-50" onClick={() => pedirConfirmacion([d.cvu])} disabled={ejecutando} data-testid={`waya-auto-aplicar-${i}`}>
                                <ShieldCheck size={12} className="mr-1" /> Aplicar
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Confirmación de ejecución real */}
      <MotorLogCard />

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!ejecutando) { setConfirmOpen(o); if (!o) setConfirmTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="waya-auto-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert size={18} /> Confirmar ejecución real</DialogTitle>
            <DialogDescription>
              Vas a ejecutar acciones reales sobre <b>{objetivos.length}</b> cuenta(s) en Waya.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <div className="flex gap-2">
              <div className="flex-1 rounded-md border border-blue-200 bg-blue-50 p-2 text-center">
                <div className="text-xl font-bold text-blue-700">{nBloqueo}</div>
                <div className="text-[10px] uppercase text-blue-500">Bloqueo preventivo</div>
              </div>
              <div className="flex-1 rounded-md border border-red-200 bg-red-50 p-2 text-center">
                <div className="text-xl font-bold text-red-700">{nCierre}</div>
                <div className="text-[10px] uppercase text-red-500">Cierre definitivo (baja)</div>
              </div>
            </div>
            <div className="text-xs text-red-800 bg-red-100 border border-red-200 rounded-md p-2 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Se crearán casos, se bloquearán/darán de baja las cuentas en Waya y se enviarán los emails. El cierre definitivo (baja de CVU) <b>no se puede deshacer</b>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setConfirmTarget(null); }} disabled={ejecutando} data-testid="waya-auto-confirm-cancel">Cancelar</Button>
            <Button onClick={confirmarEjecucion} disabled={ejecutando} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-auto-confirm-ok">
              {ejecutando ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ShieldCheck size={15} className="mr-2" />} Sí, ejecutar {objetivos.length} acción(es)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule builder dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-lg" data-testid="waya-auto-regla-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar regla' : 'Nueva regla'}</DialogTitle>
            <DialogDescription>Definí las condiciones (se cumplen todas juntas) y la acción a tomar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[60vh] overflow-auto">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: 10 montos pequeños nocturnos" className="mt-1" data-testid="waya-auto-form-nombre" />
            </div>

            <div>
              <Label className="text-xs">Tipo de persona (por prefijo de CUIT de la cuenta Waya)</Label>
              <Select value={form.tipo_persona} onValueChange={(v) => setForm(f => ({ ...f, tipo_persona: v }))}>
                <SelectTrigger className="mt-1 h-9" data-testid="waya-auto-form-tipopersona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODAS">Todas</SelectItem>
                  <SelectItem value="JURIDICA">Persona jurídica (30 / 33 / 34)</SelectItem>
                  <SelectItem value="FISICA">Persona física (20 / 23 / 24 / 27)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Condiciones</Label>
              <div className="space-y-2 mt-1">
                {form.condiciones.map((c, i) => (
                  <div key={i} className="border rounded-md p-2 space-y-1.5" data-testid={`waya-auto-cond-${i}`}>
                    <div className="flex items-center gap-1.5">
                      <Select value={c.tipo} onValueChange={(v) => setCond(i, { tipo: v, alerta: v === 'ALERTA' ? (c.alerta || 'MONTO_PEQUEÑO') : null })}>
                        <SelectTrigger className="w-36 h-9" data-testid={`waya-auto-cond-tipo-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALERTA">Alerta</SelectItem>
                          <SelectItem value="MONTO_ACUM">Monto acumulado</SelectItem>
                          <SelectItem value="CANT_MOV">Cant. movimientos</SelectItem>
                        </SelectContent>
                      </Select>
                      {c.tipo === 'ALERTA' && (
                        <Select value={c.alerta} onValueChange={(v) => setCond(i, { alerta: v })}>
                          <SelectTrigger className="flex-1 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ALERTAS.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      <span className="text-slate-400 text-sm">≥</span>
                      <Input type="number" value={c.valor} onChange={e => setCond(i, { valor: e.target.value })} className="w-20 h-9" data-testid={`waya-auto-cond-valor-${i}`} />
                      {form.condiciones.length > 1 && <button onClick={() => removeCond(i)} className="text-slate-400 hover:text-red-600"><X size={15} /></button>}
                    </div>
                    {c.tipo === 'CANT_MOV' && (
                      <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-dashed border-slate-200" data-testid={`waya-auto-cond-filtros-${i}`}>
                        <span className="text-[11px] text-slate-400">Filtros (opcional):</span>
                        <span className="text-[11px] text-slate-500">franja</span>
                        <Input type="number" min={0} max={23} placeholder="0" value={c.hora_desde ?? ''} onChange={e => setCond(i, { hora_desde: e.target.value })} className="w-14 h-8" data-testid={`waya-auto-cond-hd-${i}`} />
                        <span className="text-[11px] text-slate-400">a</span>
                        <Input type="number" min={0} max={23} placeholder="6" value={c.hora_hasta ?? ''} onChange={e => setCond(i, { hora_hasta: e.target.value })} className="w-14 h-8" data-testid={`waya-auto-cond-hh-${i}`} />
                        <span className="text-[11px] text-slate-400">hs · monto &lt;</span>
                        <Input type="number" min={0} placeholder="400000" value={c.monto_max ?? ''} onChange={e => setCond(i, { monto_max: e.target.value })} className="w-28 h-8" data-testid={`waya-auto-cond-mmax-${i}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addCond} className="mt-2 h-8 text-xs" data-testid="waya-auto-add-cond"><Plus size={13} className="mr-1" /> Agregar condición</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ventana de tiempo</Label>
                <Select value={form.ventanaTipo} onValueChange={(v) => setForm(f => ({ ...f, ventanaTipo: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="waya-auto-form-ventana"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TODO">Todo lo pendiente</SelectItem>
                    <SelectItem value="HORAS">Últimas N horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.ventanaTipo === 'HORAS' && (
                <div>
                  <Label className="text-xs">Horas</Label>
                  <Input type="number" value={form.ventanaHoras} onChange={e => setForm(f => ({ ...f, ventanaHoras: e.target.value }))} className="mt-1 h-9" data-testid="waya-auto-form-horas" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Acción</Label>
                <Select value={form.accion} onValueChange={(v) => setForm(f => ({ ...f, accion: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="waya-auto-form-accion"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BLOQUEO">Bloqueo preventivo</SelectItem>
                    <SelectItem value="CIERRE">Cierre definitivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Modo</Label>
                <Select value={form.modo} onValueChange={(v) => setForm(f => ({ ...f, modo: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="waya-auto-form-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PROPONER">Proponer (aprobación 1 clic)</SelectItem>
                    <SelectItem value="EJECUTAR">Ejecutar automáticamente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} disabled={saving} data-testid="waya-auto-form-cancelar">Cancelar</Button>
            <Button onClick={guardarRegla} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="waya-auto-form-guardar">
              {saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WayaReglasAuto;
